import { SubscriptionBillingCycleEnum, SubscriptionPlanCategoryEnum } from "src/utils/enums/PartnerSubscription";
import { SubscriptionPlanInterface } from "src/utils/interfaces/payment/SubscrptionPlanInterface";
import { DISCOUNT_PERCENTAGE_PER_EXTRA_JOB, isAICVSearchEnabled, VAT_PERCENTAGE_IN_SAUDI } from "src/utils/constants/constants";
import { enterprisePlan } from "src/utils/constants/enterprisePlan";
import { getPageSearchParams } from "src/utils/utm/classify-utm";

export type PricingProductTab = "job" | "nd" | "cv" | "combo";
export type PricingPlanKey = SubscriptionPlanCategoryEnum;

export const PRICING_V2_MAX_JOBS = 6;
export const PRICING_V2_ENTERPRISE_THRESHOLD = 6;

/** Display-only: Basic list price is 50 SAR above the loyal API unit (245 → show 295). */
export const LOYAL_BASIC_DISCOUNT_PER_JOB = 50;

export type PlanDisplayPrice = {
    original: number;
    subtotal: number;
    /** Flat loyal discount on Basic only (50 × jobs). */
    loyalSavings: number;
    /** Volume % discount applied after loyal unit price. */
    volumeSavings: number;
};

export const CONTACT_MULTIPLIER: Partial<Record<SubscriptionPlanCategoryEnum, string>> = {
    [SubscriptionPlanCategoryEnum.RISE]: "2×",
    [SubscriptionPlanCategoryEnum.GROWTH]: "3×",
};

export const SAUD_MULTIPLIER: Partial<Record<SubscriptionPlanCategoryEnum, string>> = {
    [SubscriptionPlanCategoryEnum.GROWTH]: "3×",
};

export const SHARED_FEATURES = [
    "pricing_v2_feat_ai_matching",
    "pricing_v2_feat_advanced_filters",
    "pricing_v2_feat_video_interviews",
    "pricing_v2_feat_unlimited_users",
] as const;

export const ENTERPRISE_ONLY_FEATURES = [
    "pricing_v2_feat_whatsapp",
    "pricing_v2_feat_careersite",
    "pricing_v2_feat_multi_locations",
    "pricing_v2_feat_ai_scheduling",
    "pricing_v2_feat_ai_onboarding",
] as const;

export const PRICING_V2_ND_FAQS = [
    { q: "pricing_v2_nd_faq_q_1", a: "pricing_v2_nd_faq_a_1" },
    { q: "pricing_v2_nd_faq_q_2", a: "pricing_v2_nd_faq_a_2" },
] as const;

export const PRICING_V2_FAQS = Array.from({ length: 11 }, (_, i) => ({
    q: `pricing_v2_faq_q_${i + 1}`,
    a: `pricing_v2_faq_a_${i + 1}`,
    linkKey: i === 5 ? "pricing_v2_faq_saud_link" : undefined,
    /** FAQ 11 is about AI CV Search; Combo FAQ is removed. */
    requiresAICVSearch: i >= 10,
})).filter((faq) => isAICVSearchEnabled || !faq.requiresAICVSearch);

/** 1–5 jobs: monthly. 6+: quarterly. */
export const getBillingCycleForJobs = (jobsCount: number): SubscriptionBillingCycleEnum =>
    jobsCount >= 6 ? SubscriptionBillingCycleEnum.QUARTERLY : SubscriptionBillingCycleEnum.MONTHLY;

export const getBillingCycleMonths = (jobsCount: number) => {
    if (jobsCount >= 12) return 12;
    if (jobsCount >= 6) return 3;
    return 1;
};

export const getPlanValidityI18nKey = (jobsCount: number) => {
    const months = getBillingCycleMonths(jobsCount);
    if (months >= 12) return "valid_for_1_year";
    if (months >= 3) return "valid_for_3_months";
    return "valid_for_1_month";
};

export const isEnterpriseQuantity = (jobsCount: number) => jobsCount >= PRICING_V2_ENTERPRISE_THRESHOLD;

const QUERY_SELECTABLE_PLANS: PricingPlanKey[] = [
    SubscriptionPlanCategoryEnum.CORE,
    SubscriptionPlanCategoryEnum.RISE,
    SubscriptionPlanCategoryEnum.GROWTH,
    SubscriptionPlanCategoryEnum.ENTERPRISE,
];

const stripTrailingSlash = (raw: string | null) => raw?.trim().replace(/\/+$/, "") || null;

export const parsePricingPlanQueryParam = (raw: string | null): PricingPlanKey | null => {
    const value = stripTrailingSlash(raw)?.toUpperCase() as PricingPlanKey | undefined;
    return value && QUERY_SELECTABLE_PLANS.includes(value) ? value : null;
};

export const parsePricingJobsCountQueryParam = (raw: string | null): number | null => {
    const value = stripTrailingSlash(raw);
    if (!value) return null;
    const n = Number.parseInt(value, 10);
    if (!Number.isFinite(n)) return null;
    return Math.max(1, Math.min(PRICING_V2_MAX_JOBS, n));
};

/**
 * Read jobsCount / plan / national_day from the URL, whether they sit before the hash
 * (`?jobsCount=3&plan=CORE#/payment/company/...`) or after it
 * (`#/payment/company/...?jobsCount=3&plan=CORE`).
 */
export const getPricingQueryPrefill = (): {
    plan: PricingPlanKey | null;
    jobsCount: number | null;
    ndTab: boolean;
} => {
    const params = getPageSearchParams();
    const ndTab =
        stripTrailingSlash(params.get("national_day")) === "true" ||
        stripTrailingSlash(params.get("tab"))?.toLowerCase() === "nd";
    const plan = parsePricingPlanQueryParam(params.get("plan"));
    let jobsCount = parsePricingJobsCountQueryParam(params.get("jobsCount"));

    if (ndTab) {
        return { plan, jobsCount: null, ndTab: true };
    }

    if (plan === SubscriptionPlanCategoryEnum.ENTERPRISE && jobsCount == null) {
        jobsCount = PRICING_V2_MAX_JOBS;
    }
    if (plan && plan !== SubscriptionPlanCategoryEnum.ENTERPRISE && jobsCount != null && isEnterpriseQuantity(jobsCount)) {
        jobsCount = PRICING_V2_ENTERPRISE_THRESHOLD - 1;
    }
    if (!plan && jobsCount != null && isEnterpriseQuantity(jobsCount)) {
        return { plan: SubscriptionPlanCategoryEnum.ENTERPRISE, jobsCount, ndTab: false };
    }

    return { plan, jobsCount, ndTab: false };
};

/** Total job posts shown = selected positions × jobsPerResetCycle from API. */
export const getJobPostsCount = (plan: SubscriptionPlanInterface, jobsCount: number) =>
    (plan.jobsPerResetCycle || 1) * jobsCount;

export const getJobDurationDays = (plan: SubscriptionPlanInterface) =>
    plan.jobOpenDurationDays || 30;

/** Saud credits: Rise/Growth scale with selected positions; other plans stay at the plan allowance. */
export const getSaudCredits = (plan: SubscriptionPlanInterface, jobsCount: number = 1) => {
    const base = plan.maxInterviewsResetCycle ?? 0;
    const scalesWithJobs =
        plan.category === SubscriptionPlanCategoryEnum.RISE ||
        plan.category === SubscriptionPlanCategoryEnum.GROWTH;
    return scalesWithJobs ? base * Math.max(1, jobsCount || 1) : base;
};

export const getContacts = (plan: SubscriptionPlanInterface, jobsCount: number) =>
    (plan.unlocksPerResetCycle || 0) * jobsCount;

const getUnitPrice = (plan: SubscriptionPlanInterface, jobsCount: number) => {
    const cycle = getBillingCycleForJobs(jobsCount);
    return (
        (cycle === SubscriptionBillingCycleEnum.YEARLY
            ? plan?.yearlyBillingPrice
            : plan?.monthlyBillingPrice) || 0
    );
};

/**
 * Volume tiers match the HTML prototype: 5% at 2–3 posts, 10% at 4–5.
 * Displayed net totals always round up (Math.ceil) via displayPrice.
 */
export const getVolumeDiscountPercent = (jobsCount: number) => {
    if (jobsCount >= 4) return 10;
    if (jobsCount >= 2) return 5;
    return 0;
};

/**
 * Pricing display + checkout amount for pricing-v2 / paywall-v2.
 * API unit is already final (loyal cut is server-side). Volume % still applies
 * on that unit. isLoyalCustomer only reconstructs the list "was" price for UI.
 */
export const getPlanDisplayPrice = (
    plan: SubscriptionPlanInterface,
    jobsCount: number,
    opts?: { isLoyalCustomer?: boolean }
): PlanDisplayPrice => {
    const q = Math.max(1, jobsCount || 1);
    const unit = getUnitPrice(plan, q);

    const isLoyalBasic =
        !!opts?.isLoyalCustomer && plan.category === SubscriptionPlanCategoryEnum.CORE;
    const loyalSavings = isLoyalBasic ? LOYAL_BASIC_DISCOUNT_PER_JOB * q : 0;
    const original = unit * q + loyalSavings;

    const volPct = getVolumeDiscountPercent(q) / 100;
    const afterApi = unit * q;
    const subtotal = Math.ceil(afterApi * (1 - volPct));
    const volumeSavings = afterApi - subtotal;

    return {
        original,
        subtotal,
        loyalSavings,
        volumeSavings,
    };
};

export const getDiscountHint = (
    jobsCount: number
): { key: string; d?: number; q?: number } | null => {
    if (jobsCount >= PRICING_V2_ENTERPRISE_THRESHOLD) {
        return { key: "pricing_v2_hint_custom_offer" };
    }
    if (jobsCount === 1) {
        return { key: "pricing_v2_hint_add_for_5", d: DISCOUNT_PERCENTAGE_PER_EXTRA_JOB };
    }
    if (jobsCount <= 3) {
        return {
            key: "pricing_v2_hint_5_reach_10",
            d: getVolumeDiscountPercent(jobsCount),
            q: 4,
        };
    }
    return { key: "pricing_v2_hint_10_applied", d: getVolumeDiscountPercent(jobsCount) };
};

export const sortJobPlans = (plans: SubscriptionPlanInterface[]) => {
    const order = [
        SubscriptionPlanCategoryEnum.CORE,
        SubscriptionPlanCategoryEnum.RISE,
        SubscriptionPlanCategoryEnum.GROWTH,
    ];
    return [...plans].sort(
        (a, b) => order.indexOf(a.category) - order.indexOf(b.category)
    );
};

export const filterJobPlans = (plans: SubscriptionPlanInterface[] = []) =>
    sortJobPlans(
        plans.filter(
            (p) =>
                p.category === SubscriptionPlanCategoryEnum.CORE ||
                p.category === SubscriptionPlanCategoryEnum.RISE ||
                p.category === SubscriptionPlanCategoryEnum.GROWTH
        )
    );

export const defaultSelectedPlan = (plans: SubscriptionPlanInterface[]): PricingPlanKey => {
    const premium = plans.find((p) => p.category === SubscriptionPlanCategoryEnum.RISE);
    if (premium) return SubscriptionPlanCategoryEnum.RISE;
    return plans[0]?.category || SubscriptionPlanCategoryEnum.CORE;
};

export const getPlanByKey = (
    plans: SubscriptionPlanInterface[],
    key: PricingPlanKey
): SubscriptionPlanInterface | null => {
    if (key === SubscriptionPlanCategoryEnum.ENTERPRISE) {
        return enterprisePlan;
    }
    return plans.find((p) => p.category === key) || null;
};

export const isMostPopular = (category: SubscriptionPlanCategoryEnum) =>
    category === SubscriptionPlanCategoryEnum.RISE;

export const formatPrice = (n: number) =>
    (n ?? 0).toLocaleString("en-US");

/**
 * Display rounding for plan / checkout amounts.
 * Net prices, discounts, and plan totals: always round up, no fraction.
 * VAT and checkout "total to pay": keep two decimal places, no ceil/floor.
 */
export const displayPrice = (price: number, showDecimal = false) => {
    const n = Number(price) || 0;
    if (showDecimal) {
        return parseFloat(n.toFixed(2));
    }
    return Math.ceil(n);
};

export type PlanCheckoutTotals = PlanDisplayPrice & {
    volumePercent: number;
    vatAmount: number;
    total: number;
};

/** Checkout amounts: same net as the pricing page, plus VAT on the ceiled subtotal. */
export const getPlanCheckoutTotals = (
    plan: SubscriptionPlanInterface,
    jobsCount: number,
    opts?: { isLoyalCustomer?: boolean }
): PlanCheckoutTotals => {
    const pricing = getPlanDisplayPrice(plan, jobsCount, opts);
    const vatAmount = pricing.subtotal * (VAT_PERCENTAGE_IN_SAUDI / 100);
    return {
        original: displayPrice(pricing.original),
        subtotal: displayPrice(pricing.subtotal),
        loyalSavings: displayPrice(pricing.loyalSavings),
        volumeSavings: displayPrice(pricing.volumeSavings),
        volumePercent: getVolumeDiscountPercent(jobsCount),
        vatAmount: displayPrice(vatAmount, true),
        total: displayPrice(pricing.subtotal + vatAmount, true),
    };
};

/** Flat yearly total for exclusive / National Day plans (API already applies discount). */
export const getExclusivePlanCheckoutTotals = (
    plan: SubscriptionPlanInterface
): PlanCheckoutTotals => {
    const subtotal = displayPrice(plan.finalPrice ?? plan.yearlyBillingPrice);
    const original = displayPrice(
        plan.originalPrice ?? plan.originalYearlyBillingPrice ?? subtotal
    );
    const volumeSavings = Math.max(0, original - subtotal);
    const vatAmount = subtotal * (VAT_PERCENTAGE_IN_SAUDI / 100);
    return {
        original,
        subtotal,
        loyalSavings: 0,
        volumeSavings: displayPrice(volumeSavings),
        volumePercent: plan.discountPercentage ?? 0,
        vatAmount: displayPrice(vatAmount, true),
        total: displayPrice(subtotal + vatAmount, true),
    };
};